import { useRef } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Printer } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface EventInfo {
  id: number
  name: string
  primaryColor: string | null
  secondaryColor: string | null
  font: string | null
  badgeWidth: number
  badgeHeight: number
  badgeNameSize: number | null
  badgeSecondarySize: number | null
  badgeTextPosition: number | null
  printQr: boolean
  logoUrl: string | null
  badgeBackgroundUrl: string | null
}

interface EventUser {
  token: string
  title: string | null
  firstName: string | null
  lastName: string | null
  organization: string | null
  position: string | null
  idNumber: string | null
  seatRow: string | null
  imageUrl: string | null
  customFields: Record<string, string>
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventInfo
  eventUser: EventUser
  qrCodeDataUrl: string
  seatingMapEnabled?: boolean
  urls: AppShellProps['urls'] & {
    eventUsers: string
    badge: string
    registerSeating: string
  }
  translations: AppShellProps['translations'] & {
    userDetails: string
    print: string
    back: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Show(props: Props) {
  const { event, eventUser, qrCodeDataUrl, seatingMapEnabled = false, urls, translations: t, ...shell } = props
  const badgeRef = useRef<HTMLDivElement>(null)

  const primary = event.primaryColor || '#37352F'
  const secondary = event.secondaryColor || '#91918E'
  const nameSize = event.badgeNameSize || 28
  const secondarySize = event.badgeSecondarySize || 18
  const textPadding = event.badgeTextPosition || 280
  const fontFamily = event.font || "'Poppins', sans-serif"

  const fullName = [
    eventUser.title?.toUpperCase(),
    eventUser.firstName?.toUpperCase(),
    eventUser.lastName?.toUpperCase(),
  ].filter(Boolean).join(' ')

  const handlePrint = () => {
    const el = badgeRef.current
    if (!el) return

    // Assign guest to live seating map when enabled
    if (seatingMapEnabled) {
      router.post(urls.registerSeating, {}, {
        preserveState: true,
        preserveScroll: true,
        only: [], // Don't reload anything
      })
    }

    const w = window.open('', '_blank', 'width=600,height=600')
    if (!w) return

    w.document.write(`
      <html>
      <head>
        <title>${event.name}</title>
        <style>
          @page { size: ${event.badgeWidth}in ${event.badgeHeight}in; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; font-family: ${fontFamily}; }
          body { width: ${event.badgeWidth}in; height: ${event.badgeHeight}in; }
        </style>
      </head>
      <body>${el.innerHTML}</body>
      </html>
    `)
    w.document.close()

    if (event.badgeBackgroundUrl) {
      const img = new Image()
      img.onload = () => {
        setTimeout(() => { w.print() }, 300)
      }
      img.onerror = () => { w.print() }
      img.src = event.badgeBackgroundUrl
    } else {
      setTimeout(() => { w.print() }, 300)
    }
  }

  return (
    <AppShell urls={urls} translations={t} {...shell}>
      <Head title={`${t.userDetails} — ${event.name}`} />

      <div className="mx-auto max-w-2xl px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a
              href={urls.eventUsers}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[#F1F1EF] transition-colors"
            >
              <ArrowLeft className="h-4 w-4 text-[#91918E]" />
            </a>
            <h1 className="text-xl font-semibold text-[#37352F]">{t.userDetails}</h1>
          </div>
          <Button onClick={handlePrint} className="bg-[#2383E2] text-white hover:bg-[#1b6abf]">
            <Printer className="mr-2 h-4 w-4" />
            {t.print}
          </Button>
        </div>

        {/* Badge Card */}
        <div className="flex justify-center">
          <div
            ref={badgeRef}
            style={{
              width: `${event.badgeWidth * 1.5}in`,
              height: `${event.badgeHeight * 1.5}in`,
              fontFamily,
              position: 'relative',
              overflow: 'hidden',
            }}
            className="rounded-lg border border-[#E8E8E5] bg-white shadow-sm"
          >
            {/* Background */}
            {event.badgeBackgroundUrl && (
              <img
                src={event.badgeBackgroundUrl}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}

            {/* Logo (only if no background) */}
            {!event.badgeBackgroundUrl && event.logoUrl && (
              <img
                src={event.logoUrl}
                alt={event.name}
                className="absolute left-3 top-2"
                style={{ maxHeight: 100 }}
              />
            )}

            {/* Content */}
            <div
              className="relative flex flex-col items-center text-center"
              style={{ paddingTop: textPadding, marginLeft: 20, marginRight: 20 }}
            >
              {/* User image */}
              {eventUser.imageUrl && !event.printQr && (
                <img
                  src={eventUser.imageUrl}
                  alt={fullName}
                  className="mb-3 rounded object-contain"
                  style={{ width: 100, height: 100 }}
                />
              )}

              {/* Name */}
              <h2
                className="font-bold"
                style={{ fontSize: nameSize, color: primary }}
              >
                {fullName}
              </h2>

              {/* Custom Arabic fields */}
              {eventUser.customFields['اسم الشروع'] && (
                <h3 style={{ fontSize: secondarySize, color: secondary }} className="font-bold">
                  {eventUser.customFields['اسم الشروع']}
                </h3>
              )}
              {eventUser.customFields['اسم الطفل'] && (
                <h3 style={{ fontSize: nameSize, color: primary }} className="font-bold">
                  {eventUser.customFields['اسم الطفل']}
                </h3>
              )}
              {eventUser.customFields['اسم المسؤول عنه'] && (
                <h3 style={{ fontSize: secondarySize, color: secondary }} className="font-bold">
                  {eventUser.customFields['اسم المسؤول عنه']}
                </h3>
              )}

              {/* Organization */}
              {eventUser.organization && (
                <h3 style={{ fontSize: secondarySize, color: secondary }} className="font-bold">
                  {eventUser.organization.toUpperCase()}
                </h3>
              )}

              {/* ID Number */}
              {eventUser.idNumber && (
                <h3 style={{ fontSize: secondarySize, color: secondary }} className="font-bold">
                  {eventUser.idNumber}
                </h3>
              )}

              {/* Seat row */}
              {eventUser.seatRow && (
                <h3 className="absolute font-bold" style={{ top: '67%', color: primary }}>
                  {eventUser.seatRow}
                </h3>
              )}

              {/* QR code */}
              {event.printQr && (
                <img
                  src={qrCodeDataUrl}
                  alt="QR Code"
                  className="mt-2"
                  style={{ width: 80 }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  )
}
